import React from 'react';
import { Box, Paper, Skeleton, Typography } from '@mui/material';
import DotWaveLoader from '../components/commonComponents/DotWaveLoader';
import DataTable from '../components/data_table/DataTable';
import EmptyPerformanceChart from './EmptyPerformanceChart';
import { performancePanelSx } from '../styles/performanceMsiStyles';

const Bar = ({ width = '100%', height = 12 }) => <Skeleton aria-hidden="true" animation="wave" variant="rounded" width={width} height={height} />;
const comparisonColumns = { xs: 'minmax(0, 1fr)', lg: 'repeat(3, minmax(0, 1fr))' };

// Match the actual summary, chart and table while their first inputs arrive.
// Background refreshes leave confirmed results in place.
export default function EarningsPerformanceSkeleton({ windows, windowTints, showGroups = false, loading = true, eventColumns = [] }) {
    if (!loading) return <Box aria-label="Empty earnings results" sx={{ display: 'grid', gap: 1.5, minWidth: 0 }}>
        <Typography variant="subtitle1">Earnings summary</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: comparisonColumns, gap: 1.5 }}>
            {Object.entries(windows).map(([key, label]) => <Paper key={key} component="section" aria-label={`Earnings window ${label}`} sx={{ ...performancePanelSx, p: 0.75, minWidth: 0, bgcolor: windowTints[key] }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}><Typography variant="caption">{label}</Typography><Typography sx={{ fontSize: 17 }}>—</Typography></Box>
                <Typography variant="caption" color="text.secondary">Factor — · Idio —</Typography>
            </Paper>)}
        </Box>
        <Paper sx={{ ...performancePanelSx, p: 1 }}><EmptyPerformanceChart label="Earnings event contribution chart" /></Paper>
        <Paper sx={{ ...performancePanelSx, p: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ mb: 0.5 }}>Event outcomes</Typography>
            <DataTable aria-label="Earnings event outcomes" data={[]} columns={eventColumns} emptyRowCount={8} wrapHeaders />
        </Paper>
    </Box>;
    return <Box role="status" aria-label="Loading earnings results" aria-busy="true" sx={{ display: 'grid', gap: 1.5, minWidth: 0,
        '@media (prefers-reduced-motion: reduce)': { '& .MuiSkeleton-root, & .MuiSkeleton-root::after': { animation: 'none' } },
    }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Typography variant="subtitle1">Earnings summary</Typography><Bar width={150} />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: comparisonColumns, gap: 1.5 }}>
            {Object.entries(windows).map(([key, label]) => <Paper key={key} sx={{ ...performancePanelSx, p: 0.75, minWidth: 0, bgcolor: windowTints[key], display: 'grid', gap: 0.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}><Typography variant="caption" sx={{ fontSize: 12 }}>{label}</Typography><Bar width={64} height={22} /></Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}><Bar width="45%" /><Bar width="35%" /></Box>
            </Paper>)}
        </Box>
        <DotWaveLoader height={320} decorative />
        <Bar width={130} height={30} />
        {showGroups && <Box sx={{ display: 'grid', gridTemplateColumns: comparisonColumns, gap: 1.5 }}>
            {Object.keys(windows).map((key) => <DotWaveLoader key={key} height={210} decorative />)}
        </Box>}
        <Paper sx={{ ...performancePanelSx, p: 1, minWidth: 0, overflow: 'hidden' }}>
            <Typography variant="subtitle1" sx={{ mb: 0.5 }}>Event outcomes</Typography>
            {Array.from({ length: 9 }, (_, index) => <Box key={index} sx={{ display: 'grid', gridTemplateColumns: 'minmax(100px, 1.5fr) repeat(3, minmax(60px, 1fr))', gap: 1.5, alignItems: 'center', height: index ? 28 : 56, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Bar width="70%" />{Object.keys(windows).map((key) => <Box key={key} sx={{ p: 0.5, bgcolor: windowTints[key] }}><Bar width="75%" /></Box>)}
            </Box>)}
        </Paper>
    </Box>;
}
